/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import { FlexContainer, SVGIcon, theme } from "@nordcloud/gnui";
import { ExternalLinkWithIndicator } from "~/components/ExternalLink";
import { useAccess, ACTIONS } from "~/services/auth/RBAC";
import { isString, MixedValue } from "~/tools";
import { DetailCopyClipboard } from "./DetailCopyClipboard";
import { DetailLink } from "./DetailLink";
import { DetailPopup } from "./DetailPopup";
import { DetailsDescription, DetailsItem, DetailsTitle } from "./styles";

export type DetailData = {
  label: string;
  value: MixedValue;
  activatedIcon?: boolean;
  activated?: boolean;
  search?: boolean;
  popup?: boolean;
  clipboard?: boolean;
  target?: string | null;
  url?: string | null;
  showFragment?: boolean;
  fragment?: React.ReactNode;
  urlTooltip?: string | null;
};

type Props = {
  detail: DetailData;
};

export function Detail({ detail }: Props) {
  const { hasAccess } = useAccess(ACTIONS.visitEstateRecords);

  const hidePopup = !detail.popup;
  const showLink = hasAccess && detail.target != null;
  const svgIconName = detail.activated ? "cloudSuccess" : "cloudError";
  const urlTooltip = detail.urlTooltip ?? "";

  return (
    <DetailsItem>
      <DetailsTitle>{detail.label}</DetailsTitle>
      <DetailsDescription>
        {detail.activatedIcon ? (
          <SVGIcon
            style={{ float: "left", marginRight: theme.spacing.spacing02 }}
            name={svgIconName}
          />
        ) : (
          detail.showFragment && detail.fragment
        )}
        <If condition={hidePopup}>
          <Then>
            <If condition={showLink}>
              <Then>
                <DetailLink
                  target={detail.target ?? ""}
                  search={detail.search ?? false}
                  value={isString(detail.value) ? detail.value : ""}
                />
              </Then>
              <Else>
                <FlexContainer columnGap={theme.spacing.spacing02}>
                  <div css={{ whiteSpace: "pre-wrap" }}>
                    <>{detail.value}</>
                  </div>
                  {detail.clipboard && (
                    <DetailCopyClipboard
                      value={isString(detail.value) ? detail.value : ""}
                    />
                  )}
                </FlexContainer>
              </Else>
            </If>
            {detail.url && (
              <ExternalLinkWithIndicator
                url={detail.url}
                css={{ marginLeft: theme.spacing.spacing02 }}
                caption={urlTooltip}
              />
            )}
          </Then>
          <Else>
            <DetailPopup label={detail.label} value={detail.value} />
          </Else>
        </If>
      </DetailsDescription>
    </DetailsItem>
  );
}
